import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Stack,
  CircularProgress,
  Divider,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import { useTranslation } from "react-i18next";
import { getTopicDataLV } from "../../../api/topicDataAPI";
import { getEventStatus } from "../../../api/eventStatusAPI";
import { getNodeConfigurationsByNodeId } from "../../../api/nodeConfigurationAPI";
import { ConvertOIDValueToString } from "../../../utils/objectConverter";
import { useRefreshInterval } from "../../../contexts/RefreshIntervalContext";
import { usePageVisibility } from "../../../hooks/usePageVisibility";
import { getPlaceIdFromPathname } from "../../../utils/pathUtils";
import { useObjectData } from "../../../hooks/useLocalStorageData";

// Each cylinder pairs a numeric Water Level (0-100) with its Level Sensor Status.
// runModeCode is the "Sensor Options" config (Sensor N Run Mode); a sensor is
// only shown when this config is present and NOT Passive (-4).
const SENSORS = [
  { idx: 1, runModeCode: 3021, levelCode: 3009, statusCode: 3018 },
  { idx: 2, runModeCode: 3022, levelCode: 3013, statusCode: 3019 },
  { idx: 3, runModeCode: 3023, levelCode: 3014, statusCode: 3020 },
];

// The top-left battery alarm comes from the battery event (OID 3040), reported
// via event status like the level sensors.
// Enum: 6 = Normal (no chip), -109 = Low (warning), -110 = Very Low (critical).
const BATTERY_ALARM_CODE = 3040;

// Level Sensor status enum (codes 3018/3019/3020):
//  6 NORMAL | -4 PASİF | -10 SENSÖR HATASI
//  -109 SEVİYE DÜŞÜK | -110 SEVİYE ÇOK DÜŞÜK | -111 SEVİYE YÜKSEK | -112 SEVİYE ÇOK YÜKSEK
function statusVisual(rawStatus, alarmType) {
  const v = Number(rawStatus);
  const RED = { color: "#d32f2f", soft: "rgba(211,47,47,0.12)" };
  const ORANGE = { color: "#ed6c02", soft: "rgba(237,108,2,0.12)" };
  const BLUE = { color: "#1976d2", soft: "rgba(25,118,210,0.12)" };
  const GRAY = { color: "#9e9e9e", soft: "rgba(158,158,158,0.12)" };

  if (v === -4) return GRAY; // passive
  // alarmType is authoritative: 2 = alarm (red), 1 = warning (orange), 0 = normal
  // Sensor error (-10) and very-low/high also map to red as a safety net.
  if (alarmType === 2 || v === -10 || v === -110 || v === -112) return RED;
  if (alarmType === 1 || v === -109 || v === -111) return ORANGE;
  return BLUE; // normal
}

function WaterCylinder({
  name,
  statusLabel,
  statusColor,
  level,
  hasReading,
  severity,
}) {
  const fill = hasReading ? Math.max(0, Math.min(100, Number(level))) : 0;
  const { color, soft } = statusColor;
  const SeverityIcon = severity?.Icon;

  return (
    <Stack alignItems="center" spacing={1.25} sx={{ minWidth: 180 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, textAlign: "center", color: "text.secondary" }}
      >
        {name}
      </Typography>

      {/* Severity chip (Warning/Alarm). The row keeps a fixed height even when
          there is no chip, so cylinders without one stay vertically aligned. */}
      <Box
        sx={{
          height: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {severity && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1,
              py: 0.25,
              borderRadius: 5,
              bgcolor: severity.soft,
              border: `1.5px solid ${severity.color}`,
            }}
          >
            {SeverityIcon && (
              <SeverityIcon sx={{ color: severity.color, fontSize: 16 }} />
            )}
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: severity.color,
              }}
            >
              {severity.label}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Cylinder (glass capsule) */}
      <Box
        sx={(theme) => ({
          position: "relative",
          width: 150,
          height: 210,
          borderRadius: "75px / 22px",
          border: `2px solid ${theme.palette.divider}`,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(90deg,#0d1115 0%,#1a2027 50%,#0d1115 100%)"
              : "linear-gradient(90deg,#e9eef3 0%,#ffffff 50%,#e9eef3 100%)",
          overflow: "hidden",
          boxShadow:
            "inset 0 2px 10px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.08)",
        })}
      >
        {/* Water fill */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${fill}%`,
            background: `linear-gradient(180deg, ${color} 0%, ${color}d9 55%, ${color}b0 100%)`,
            transition: "height 0.7s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {/* Water surface (meniscus) */}
          {fill > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: -5,
                left: "5%",
                right: "5%",
                height: 10,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.5)",
              }}
            />
          )}
        </Box>

        {/* Glossy highlight */}
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 14,
            width: 12,
            bottom: 12,
            borderRadius: 6,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.04) 100%)",
            pointerEvents: "none",
          }}
        />

        {/* Percentage overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: fill > 52 ? "#fff" : "text.primary",
              textShadow: fill > 52 ? "0 1px 3px rgba(0,0,0,0.45)" : "none",
            }}
          >
            {hasReading ? `%${Math.round(fill)}` : "-"}
          </Typography>
        </Box>
      </Box>

      {/* Numeric value */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 600 }}
      >
        {hasReading ? `${level} / 100` : "—"}
      </Typography>

      {/* Status pill with status dot (below the capsule) */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.25,
          py: 0.4,
          borderRadius: 5,
          bgcolor: soft,
          border: `1.5px solid ${color}`,
          maxWidth: 170,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: color,
            flexShrink: 0,
            boxShadow: `0 0 6px ${color}`,
          }}
        />
        <Typography
          sx={{
            fontSize: "0.72rem",
            fontWeight: 700,
            color,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {statusLabel}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function WaterLevelCylinders() {
  const { t } = useTranslation();
  const location = useLocation();
  const isPageVisible = usePageVisibility();
  const objectData = useObjectData();
  const { interval, isRunning } = useRefreshInterval();

  const rawId = getPlaceIdFromPathname(location.pathname);
  const nodeId = rawId ? parseInt(rawId, 10) : null;

  const intervalMs = useMemo(
    () => Math.max((interval || 15) * 1000, 1000),
    [interval]
  );

  const [levels, setLevels] = useState({}); // { 3009: value, ... }
  const [statuses, setStatuses] = useState({}); // { 3018: rawValue, ... }
  // Set of Run Mode codes present in the node config (null = config not loaded yet).
  const [configCodes, setConfigCodes] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine which sensors are configured from the node's "Sensor Options"
  // config: a sensor's Run Mode (3021/3022/3023) is present in the config.
  useEffect(() => {
    if (!nodeId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const cfgs = await getNodeConfigurationsByNodeId(nodeId);
        const valByCode = {};
        (Array.isArray(cfgs) ? cfgs : []).forEach((c) => {
          valByCode[c.object_idObject] = c.value;
        });
        const present = new Set(
          SENSORS.filter((s) => {
            const v = valByCode[s.runModeCode];
            return v !== undefined && v !== null;
          }).map((s) => s.runModeCode)
        );
        if (!cancelled) setConfigCodes(present);
      } catch (err) {
        console.error("WaterLevelCylinders config error:", err);
        // Fallback: treat all sensors as configured.
        if (!cancelled) setConfigCodes(new Set(SENSORS.map((s) => s.runModeCode)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [nodeId]);

  // A sensor is shown when it is configured (Run Mode present) OR status data
  // (3018/3019/3020) is arriving for it. Reversible: drop the statuses branch to
  // go back to config-only detection.
  const activeSensors = useMemo(() => {
    if (configCodes === null) return null; // config still loading
    return SENSORS.filter(
      (s) =>
        configCodes.has(s.runModeCode) ||
        Object.prototype.hasOwnProperty.call(statuses, s.statusCode)
    );
  }, [configCodes, statuses]);

  const fetchData = useCallback(async () => {
    if (!nodeId || !isPageVisible) return;
    try {
      const [lvResult, eventResult] = await Promise.all([
        getTopicDataLV(nodeId),
        getEventStatus(),
      ]);

      // Last values: single record keyed by object code
      const lv = Array.isArray(lvResult) ? lvResult[0] : lvResult;
      const nextLevels = {};
      if (lv && typeof lv === "object") {
        SENSORS.forEach(({ levelCode }) => {
          nextLevels[levelCode] = lv[levelCode];
        });
      }
      setLevels(nextLevels);

      // Event status: pick this node's level-sensor entries + battery alarm
      const statusCodes = [
        ...SENSORS.map((s) => s.statusCode),
        BATTERY_ALARM_CODE,
      ];
      const nextStatuses = {};
      if (Array.isArray(eventResult)) {
        eventResult
          .filter(
            (item) =>
              item.nodeId === nodeId && statusCodes.includes(item.event)
          )
          .forEach((item) => {
            nextStatuses[item.event] = {
              value: item.value,
              alarmType: item.alarmType,
            };
          });
      }
      setStatuses(nextStatuses);
    } catch (err) {
      console.error("WaterLevelCylinders fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [nodeId, isPageVisible]);

  useEffect(() => {
    // Paused olsa bile veriyi bir kez çek; tekrarlı polling sadece running iken.
    fetchData();
    if (!isRunning) return undefined;
    const id = setInterval(fetchData, intervalMs);
    return () => clearInterval(id);
  }, [fetchData, intervalMs, isRunning]);

  const cylinders = useMemo(
    () =>
      (activeSensors || []).map(({ idx, levelCode, statusCode }) => {
        const rawLevel = levels[levelCode];
        const numLevel = Number(rawLevel);
        const hasReading =
          rawLevel !== null &&
          rawLevel !== undefined &&
          !Number.isNaN(numLevel) &&
          numLevel >= 0;

        // Status: default to NORMAL (6) when no active event for this node
        const statusEntry =
          statusCode in statuses ? statuses[statusCode] : null;
        const rawStatus = statusEntry ? statusEntry.value : 6;
        const alarmType = statusEntry ? statusEntry.alarmType : 0;
        const statusObj = objectData.find((o) => o.code === statusCode);
        const sensorName =
          statusObj?.name || `Level Sensor-${idx} Status`;
        let statusLabel;
        try {
          statusLabel = ConvertOIDValueToString(
            { Type: statusObj?.type },
            rawStatus
          );
        } catch (e) {
          statusLabel = String(rawStatus);
        }

        // Severity chip from alarmType — only Warning (1) and Error (2).
        // Normal (0) and Passive show no chip.
        let severity = null;
        if (alarmType === 2) {
          severity = {
            label: t("summary.alarm", "Alarm"),
            color: "#d32f2f",
            soft: "rgba(211,47,47,0.12)",
            Icon: ErrorOutlineRoundedIcon,
          };
        } else if (alarmType === 1) {
          severity = {
            label: t("summary.warning", "Warning"),
            color: "#ed6c02",
            soft: "rgba(237,108,2,0.12)",
            Icon: WarningAmberRoundedIcon,
          };
        }

        return {
          key: levelCode,
          name: sensorName,
          statusLabel: statusLabel ?? "-",
          statusColor: statusVisual(rawStatus, alarmType),
          level: hasReading ? numLevel : null,
          hasReading,
          severity,
        };
      }),
    [activeSensors, levels, statuses, objectData, t]
  );

  // Battery alarm (OID 3040) arrives via event status, like the level sensors.
  // -110 (Very Low) → critical, -109 (Low) → warning, 6/Normal → no chip.
  const batteryAlarm = statuses[BATTERY_ALARM_CODE];
  const batteryAlarmType = batteryAlarm?.alarmType;
  const batteryAlarmVal =
    batteryAlarm != null ? Number(batteryAlarm.value) : null;

  let batteryStatus = null;
  if (batteryAlarmType === 2 || batteryAlarmVal === -110) {
    batteryStatus = {
      label: t("summary.critical", "Critical"),
      color: "#d32f2f",
      soft: "rgba(211,47,47,0.12)",
    };
  } else if (batteryAlarmType === 1 || batteryAlarmVal === -109) {
    batteryStatus = {
      label: t("summary.warning", "Warning"),
      color: "#ed6c02",
      soft: "rgba(237,108,2,0.12)",
    };
  }

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, height: "100%" }}>
      {/* Battery alarm/warning — shown only when the battery is low */}
      {batteryStatus && (
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.4,
              borderRadius: 5,
              bgcolor: batteryStatus.soft,
              border: `1.5px solid ${batteryStatus.color}`,
            }}
          >
            <BatteryAlertIcon
              sx={{ color: batteryStatus.color, fontSize: 18 }}
            />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: batteryStatus.color,
              }}
            >
              {t("summary.battery", "Battery")} {batteryStatus.label}
            </Typography>
          </Box>
        </Box>
      )}

      {loading || activeSensors === null ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : cylinders.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("summary.noActiveSensors", "Aktif sensör yok")}
          </Typography>
        </Box>
      ) : (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 2, sm: 4 }}
          justifyContent="space-around"
          alignItems={{ xs: "center", sm: "flex-start" }}
          useFlexGap
          sx={{ rowGap: 3, flexWrap: { xs: "nowrap", sm: "wrap" } }}
          divider={
            <Divider
              flexItem
              sx={{ display: { xs: "block", sm: "none" } }}
            />
          }
        >
          {cylinders.map((c) => (
            <WaterCylinder
              key={c.key}
              name={c.name}
              statusLabel={c.statusLabel}
              statusColor={c.statusColor}
              level={c.level}
              hasReading={c.hasReading}
              severity={c.severity}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
}
